/**
 * RTWO Asset & Token Engine — Metadata Service
 */

import type { AssetId, MetadataId, RTWOTokenMetadata } from '../domain-models';
import type { IMetadataService, MetadataResult } from '../service-interfaces';
import type { ASSRepositories } from '../repositories/ass-repositories';
import type { StorageAdapterRegistry } from '../registry/adapter-registries';
import { ASSTInternalEventTypes } from '../events/ass-event-bus';
import { BaseASSService, type ASSServiceContext } from './base-ass-service';

export class MetadataService extends BaseASSService implements IMetadataService {
  readonly serviceId = 'ass-metadata-service-001';
  readonly serviceName = 'Metadata Service';
  readonly description =
    'Metadata service — placeholder, requires real Metadata Storage. No fake metadata.';
  readonly integrations = ['Core Engine', 'Storage Abstraction Layer', 'Indexer Engine'];
  readonly requiresLedgerConnection = false;
  readonly requiresTokenRuntime = false;

  constructor(
    context: ASSServiceContext,
    private readonly repositories: ASSRepositories,
    private readonly storageRegistry: StorageAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_ledger_connection';
  }

  async configureMetadata(
    assetId: AssetId,
    metadataReference: string,
    schemaVersion: string
  ): Promise<MetadataResult> {
    if (!this.storageRegistry.isConfigured()) {
      return {
        success: false,
        metadataId: null,
        error: 'Metadata configuration requires real Metadata Storage — Not Configured',
        isPlaceholder: true,
        source: 'Asset & Token Engine — Metadata Storage Not Configured',
      };
    }

    const adapterResult = await this.storageRegistry
      .getActiveAdapter()
      .configureMetadata(assetId, metadataReference, schemaVersion);

    if (adapterResult?.metadata) {
      this.repositories.metadata.save(adapterResult.metadata);
      this.context.eventBus.emit(ASSTInternalEventTypes.METADATA_CONFIGURED, {
        metadataId: adapterResult.metadata.metadataId,
        assetId,
      });
    }

    return {
      success: false,
      metadataId: null,
      error: 'Metadata configuration requires real Metadata Storage — Not Configured',
      isPlaceholder: true,
      source: 'Asset & Token Engine — Metadata Storage Not Configured',
    };
  }

  getAllMetadata(): RTWOTokenMetadata[] {
    if (!this.storageRegistry.isConfigured()) return [];
    return this.repositories.metadata.findAll();
  }

  getMetadataByAssetId(assetId: AssetId): RTWOTokenMetadata | null {
    if (!this.storageRegistry.isConfigured()) return null;
    return this.repositories.metadata.findByAssetId(assetId);
  }

  getMetadata(metadataId: MetadataId): RTWOTokenMetadata | null {
    if (!this.storageRegistry.isConfigured()) return null;
    return this.repositories.metadata.findById(metadataId);
  }

  getMetadataCount(): 0 {
    return 0;
  }
}
